import { observer } from 'mobx-react-lite';
import DigitalHumanStore from '@/stores/digital-human-store';

// ─── Easy customisation ──────────────────────────────────────────────────────
// Node size:   change the h-*/w-* classes on the <div className="rounded-full"> line
// Label size:  change the text-* class on the <span> below each node
// Colors:      search for "violet" – replace with any Tailwind color (e.g. "purple", "indigo")
// Spacing:     change the mx-* and w-* on the connector <div> line
// ─────────────────────────────────────────────────────────────────────────────

const PHASE_LABELS = ['Rolle', 'Skills', 'Gaps', 'Ziele', 'Aktionsplan'];

interface ProgressTrackerProps {
  store: DigitalHumanStore;
}

export const ProgressTracker = observer(({ store }: ProgressTrackerProps) => {
  if (!store.progressBarRevealed) {
    return null;
  }

  const currentIdx = store.coachingPhaseIndex; // -1 while still in greeting/close
  const total = PHASE_LABELS.length;

  return (
    <div className="pointer-events-none absolute left-0 right-0 top-0 z-[80] px-6 pb-3 pt-5">
      <div className="mx-auto flex items-center justify-center">
        {PHASE_LABELS.map((label, i) => {
          const isCompleted = store.visitedPhaseIndices.has(i) && i < currentIdx;
          const isCurrent = i === currentIdx;

          return (
            <div key={label} className="flex items-center">
              <div className="flex flex-col items-center gap-1.5">
                {/* Node — change h/w classes to resize, "violet" to recolor */}
                <div
                  className={`flex h-4 w-4 items-center justify-center rounded-full transition-all duration-500 ${
                    isCurrent
                      ? 'scale-125 bg-violet-500 shadow-[0_0_12px_3px_rgba(139,92,246,0.6)]'
                      : isCompleted
                        ? 'bg-violet-400/40 border border-violet-400/50'
                        : 'border border-white/50 bg-white/25'
                  }`}
                />
                {/* Label — change text-* to resize, "violet" to recolor */}
                <span
                  className={`whitespace-nowrap text-sm font-semibold tracking-wide transition-all duration-500 ${
                    isCurrent || isCompleted
                      ? 'text-violet-500'
                      : 'text-white/70'
                  }`}
                >
                  {label}
                </span>
              </div>

              {/* Connector — change w-* to lengthen/shorten */}
              {i < total - 1 && (
                <div className="mx-3 h-px w-10 bg-white/50 transition-all duration-500" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
